import React from 'react';
import {
  GuidePage, Section, Steps, QuestionList, WhatHappensNext, Trouble, Footnote, Para, Bullets, Callout,
  startCaseStep, subjectRow, troubleSleeveRows, TROUBLE_CATEGORY_MISSING, TROUBLE_WRONG_HOUSEHOLD,
} from '../GuideKit';

/* Trading → Raise / Invest Cash → Invest Cash.
 *
 * Written against the live flow Prosperity_Workflow_Create_New_Case_Trading
 * (version 1, activated 2026-10-04): screens "Invest Cash Details", "Invest
 * Cash Pending Funds" and "Invest Cash Accounts". The registration table on
 * the first screen lists active registrations on the household; the sleeve
 * table on the second lists active sleeves under that registration, minus
 * Distribution, Contribution and CNX sleeves. Raise Cash shares the category
 * but is a different task, so it has its own page (RaiseCash.js). Shared
 * chrome and policy copy live in GuideKit.js.
 */

const STEPS = [
  startCaseStep({
    recordType: 'Trading',
    category: 'Raise / Invest Cash',
    subCategory: 'Invest Cash',
    subject: subjectRow('Invest cash - 0989-022356'),
    lookups: [['Household', 'The client household. It decides which registrations and sleeves you can pick later, and the advisor and office are taken from it.']],
  }),
  {
    title: 'Answer the Invest Cash Details questions',
    body: (
      <>
        <p style={{ marginBottom: '0.5rem' }}>
          Questions reveal themselves one at a time, and every one is required once it appears. The first asks
          whether the funds are available or still pending - <strong>if they are pending, stop</strong>: the next
          screen, <em>Invest Cash Pending Funds</em>, says the team is unable to allocate or trade an account while
          funds are pending / not settled, and asks you to open a new Case once they have settled. Cancel and come
          back then; do not continue past it.
        </p>
        <p style={{ margin: 0 }}>
          The details box, <em>Please enter any other details the Trading Team may need...</em>, becomes the case
          description. If you will be journaling cash between sleeves or investing in more than one sleeve, put the
          exact amount for each sleeve here - there is no other text box later.
        </p>
      </>
    ),
  },
  {
    title: 'Pick the registration',
    body: (
      <p style={{ margin: 0 }}>
        Once the details box has something in it, a note explaining what Invest Cash is for appears, and under it
        the <strong>Accounts / Registrations</strong> table: Account Number, Account Details, Total Cash and Total
        Value, with a search bar. Pick exactly one row - the registration the cash is sitting in - and click Next.
      </p>
    ),
  },
  {
    title: 'Choose the sleeves to invest into',
    body: (
      <p style={{ margin: 0 }}>
        The <strong>Invest Cash Accounts</strong> screen lists the sleeves under that registration in the{' '}
        <strong>Invest Cash Account Selections</strong> table: Account, Account Details, Model, Cash and Balance.
        You can tick more than one. The sleeves you tick are recorded in the case details for the Trading team.
      </p>
    ),
  },
  {
    title: 'Acknowledge and submit',
    body: (
      <p style={{ margin: 0 }}>
        Read the Operations and Trading notice under the table - it governs timing and what the teams can and
        cannot do. Select <em>I have read, understand, and agree to the above information</em> under
        Acknowledgement and click Next. FSC creates the case, stamped with the registration and the amount, and
        opens it. Note the case number for follow-up.
      </p>
    ),
  },
];

// Every question on the two screens, in the order it can appear.
const QUESTIONS = [
  {
    screen: 'Invest Cash Details',
    q: 'Are these available funds, or are they still pending?',
    when: 'Always - this is the first question',
    options: ['Available Funds (Cash has settled and is ready for Trading)', 'Pending (The funds have not settled)'],
    note: 'Pending ends the questions on this screen, and Next takes you to an Invest Cash Pending Funds message instead. Open the case once the cash has settled.',
    warn: true,
  },
  {
    q: 'Invest Cash Amount',
    when: 'Only if you chose Available Funds',
    note: 'Recorded as the amount on the case.',
  },
  {
    q: 'To cover management fees, all accounts must maintain 1-2% cash, as a result, the full amount may not be invested if it would put this account under that threshold',
    when: 'Once the amount entered is more than $20',
    options: ['I understand that the full amount may not be invested in order to cover management fees'],
  },
  {
    q: 'Please enter any other details the Trading Team may need...',
    when: 'Once you have ticked the management fee acknowledgement above',
    note: 'Becomes the case description. Give exact amounts per sleeve if you are splitting the cash or journaling it from one sleeve to another.',
  },
  {
    q: 'Accounts / Registrations',
    when: 'Once the details box has something in it',
    note: 'Single select - pick exactly one registration. Columns: Account Number, Account Details, Total Cash, Total Value. Lists the household’s active registrations, highest Total Value first.',
  },
  {
    screen: 'Invest Cash Accounts',
    q: 'Invest Cash Account Selections',
    when: 'Always',
    note: 'Multi select. Columns: Account, Account Details, Model, Cash, Balance. Lists the active sleeves under the registration you picked, largest balance first.',
  },
  {
    q: 'Acknowledgement',
    when: 'Always, under the Operations and Trading notice',
    options: ['I have read, understand, and agree to the above information'],
  },
];

const TROUBLE = [
  TROUBLE_CATEGORY_MISSING('Trading', 'Raise / Invest Cash'),
  {
    h: 'I got an "Invest Cash Pending Funds" message',
    p: [
      'You said the funds are pending. The Trading team cannot allocate or trade cash that has not settled. Cancel this request and submit a new case once the cash is available.',
    ],
  },
  {
    h: "The registration table hasn't appeared",
    p: ['It stays hidden until the details box above it has something in it.'],
  },
  {
    h: "The registration I expected isn't in Accounts / Registrations",
    p: ['Only active registrations on the household chosen on the first screen are listed.'],
  },
  troubleSleeveRows('Invest Cash Account Selections only lists active sleeves under the registration you picked on the previous screen. Distribution and Contribution sleeves, and cancelled sleeves ending in CNX, are left out. If the sleeve sits under a different registration, go Back and pick that one.'),
  TROUBLE_WRONG_HOUSEHOLD,
];

export default function InvestCash() {
  return (
    <GuidePage
      title="Invest Cash Requests"
      icon="graph-up-arrow"
      path="Trading → Raise / Invest Cash → Invest Cash"
      minutes={4}
      intro="How to ask the Prosperity Trading team to invest settled cash in a client account - what to gather first, every question you will be asked, and what happens after you submit."
    >
      <Section title="Before you start" icon="clipboard-check">
        <Para>Have these ready. The form branches on your answers.</Para>
        <Bullets items={[
          <><strong>Settled cash.</strong> The funds must be available, not pending - otherwise the request cannot go ahead.</>,
          <><strong>The registration the cash is in</strong> and <strong>the sleeve or sleeves to invest it into</strong>. You pick both from lists.</>,
          <><strong>The amount to invest</strong>, and how to split it if more than one sleeve is involved.</>,
        ]} />
        <Callout>
          In the form&rsquo;s own words: <em>Invest Cash is used to buy EXISTING / SETTLED Cash into an EXISTING
          Strategy / Position.</em> For example, an ACH has recently come into the account and you want it allocated
          to the account&rsquo;s current model. For any other trade request, choose a different category.
        </Callout>
        <Callout tone="warn">
          All accounts must keep 1-2% cash to cover management fees, so the full amount may not be invested if it
          would take the account under that level. The form asks you to acknowledge this.
        </Callout>
      </Section>

      <Section title="Submitting the request" icon="list-ol">
        <Steps steps={STEPS} />
      </Section>

      <Section title="Every question, and when it appears" icon="question-circle">
        <Para>Use this to prepare before you sit down with a client, or to work out why a question you expected has not shown up.</Para>
        <QuestionList questions={QUESTIONS} />
      </Section>

      <WhatHappensNext team="trading" />

      <Trouble items={TROUBLE} />

      <Footnote>
        This guide describes the request form only. It is not investment advice, and it does not describe how
        the Trading team places the trades.
      </Footnote>
    </GuidePage>
  );
}
